import React from "react";
import { createPortal } from "react-dom";
import { LuChevronLeft, LuChevronRight, LuX } from "react-icons/lu";
import type { SectionCfg, SectionOption, SectionOptions, Tweaks } from "./types";
import { useCreatorData } from "./templateData";
import { buildFeaturedCarouselSelectOptions } from "./recentContentCarouselOptions";
import { COMPONENTS, getPageDef } from "./pageRegistry";
import { mergeSectionCfgFromContentEdits } from "./sectionContentEdits";
import {
  defaultSchemaCfg,
  extraSectionsOf,
  schemaBlockTypeForCatalog,
} from "./customWebsitePages";
import { drawerSlideClass, useDrawerSlide } from "./useDrawerSlide";
import { useRegisterInspectorNestedDrawer } from "./inspectorNestedDrawer";
import {
  DEFAULT_JOURNAL_GRID_HEADING,
  requiredFieldLabel,
} from "./websiteInspectorFieldLabels";

function resolveInspectorDrawerHost(
  anchor: HTMLElement | null,
): HTMLElement | null {
  if (!anchor) return null;
  return (
    (anchor.closest(".mte-we-inspector") as HTMLElement | null) ||
    (anchor.closest(".mte-lib-edit-host") as HTMLElement | null) ||
    (anchor.closest(".mte-we-inspector-col") as HTMLElement | null) ||
    null
  );
}

function optionList(
  o: SectionOption,
  carousels: ReturnType<typeof useCreatorData>["carousels"],
): { value: string; label: string }[] {
  if (o.dynamicOptions === "recentContentCarousels") {
    return buildFeaturedCarouselSelectOptions(carousels);
  }
  return (o.options || []).map((opt) => {
    if (typeof opt === "object" && opt != null && "value" in opt) {
      return { value: String(opt.value), label: String(opt.label) };
    }
    return { value: String(opt), label: String(opt) };
  });
}

export function InspectorToggle({
  on,
  onChange,
  disabled,
  label,
}: {
  on: boolean;
  onChange: (v: boolean) => void;
  disabled?: boolean;
  label?: string;
}) {
  return (
    <button
      type="button"
      className={`mte-we-switch${on ? " is-on" : ""}${disabled ? " is-disabled" : ""}`}
      role="switch"
      aria-checked={on}
      aria-label={label}
      disabled={disabled}
      onClick={() => {
        if (!disabled) onChange(!on);
      }}
    >
      <span />
    </button>
  );
}

export function InspectorToggleRow({
  label,
  on,
  onChange,
}: {
  label: string;
  on: boolean;
  onChange: (v: boolean) => void;
}) {
  return (
    <div className="mte-we-toggle-row">
      <span>{label}</span>
      <InspectorToggle on={on} onChange={onChange} />
    </div>
  );
}

export function InspectorNumberRow({
  label,
  value,
  onChange,
  min = 1,
  max = 50,
}: {
  label: string;
  value: number;
  onChange: (v: number) => void;
  min?: number;
  max?: number;
}) {
  return (
    <div className="mte-we-number-row">
      <span>{label}</span>
      <input
        type="number"
        min={min}
        max={max}
        value={Number.isFinite(value) ? value : min}
        onChange={(e) =>
          onChange(Math.max(min, Math.min(max, Number(e.target.value) || min)))
        }
      />
    </div>
  );
}

export function InspectorDropdown({
  label,
  value,
  options,
  onChange,
  hint,
}: {
  label: string;
  value: string;
  options: { value: string; label: string }[];
  onChange: (v: string) => void;
  hint?: string;
}) {
  const [open, setOpen] = React.useState(false);
  const rootRef = React.useRef<HTMLDivElement | null>(null);
  const [host, setHost] = React.useState<HTMLElement | null>(null);
  const slide = useDrawerSlide(open);
  const current = options.find((o) => o.value === value) || options[0];

  useRegisterInspectorNestedDrawer(
    `options-drawer:${label}`,
    open,
    () => setOpen(false),
  );

  React.useEffect(() => {
    if (!slide.present) {
      setHost(null);
      return;
    }
    const el = resolveInspectorDrawerHost(rootRef.current);
    setHost(el);
    if (el) el.classList.add("is-picker-open");
    return () => {
      el?.classList.remove("is-picker-open");
    };
  }, [slide.present]);

  React.useEffect(() => {
    if (!open) return;
    window.dispatchEvent(new Event("mte-we-exclusive-open"));
    const onKey = (e: KeyboardEvent) => {
      if (e.key === "Escape") setOpen(false);
    };
    const onOther = () => setOpen(false);
    window.addEventListener("keydown", onKey);
    window.addEventListener("mte-we-exclusive-open", onOther);
    return () => {
      window.removeEventListener("keydown", onKey);
      window.removeEventListener("mte-we-exclusive-open", onOther);
    };
  }, [open]);

  const drawer =
    slide.present && host
      ? createPortal(
          <div
            className={drawerSlideClass(
              "right",
              slide.closing,
              "mte-we-picker-drawer mte-we-options-drawer",
            )}
            role="dialog"
            aria-modal="true"
            aria-label={label}
            onAnimationEnd={slide.onAnimationEnd}
          >
            <div className="mte-we-picker-drawer-bar">
              <button
                type="button"
                className="mte-we-picker-drawer-back"
                onClick={() => setOpen(false)}
              >
                <LuChevronLeft size={16} aria-hidden />
                Back
              </button>
              <button
                type="button"
                className="mte-we-picker-drawer-x"
                aria-label="Close"
                onClick={() => setOpen(false)}
              >
                <LuX size={18} />
              </button>
            </div>
            <div className="mte-we-options-drawer-body">
              <div className="mte-we-picker-title">{label}</div>
              <div className="mte-we-options-drawer-list" role="listbox">
                {options.map((o) => (
                  <button
                    key={o.value}
                    type="button"
                    role="option"
                    aria-selected={o.value === value}
                    className={`mte-we-options-drawer-item${
                      o.value === value ? " is-on" : ""
                    }`}
                    onClick={() => {
                      onChange(o.value);
                      setOpen(false);
                    }}
                  >
                    <span>{o.label}</span>
                    {o.value === value ? (
                      <span className="mte-we-options-drawer-check" aria-hidden>
                        ✓
                      </span>
                    ) : null}
                  </button>
                ))}
              </div>
            </div>
          </div>,
          host,
        )
      : null;

  return (
    <div className="mte-we-drop" ref={rootRef}>
      <div className="mte-we-field-label">{label}</div>
      <button
        type="button"
        className={`mte-we-drop-btn${open ? " is-open" : ""}`}
        aria-haspopup="dialog"
        aria-expanded={open}
        onClick={() => setOpen((v) => !v)}
      >
        <span>{current?.label || value}</span>
        <LuChevronRight aria-hidden />
      </button>
      {hint ? <div className="mte-we-field-hint">{hint}</div> : null}
      {drawer}
    </div>
  );
}

type FieldProps = {
  label: string;
  value: string;
  onChange: (v: string) => void;
  placeholder?: string;
  multiline?: boolean;
  reqField?: string;
  invalid?: boolean;
  immediate?: boolean;
};

export function InspectorSectionOptions({
  options,
  cfg,
  onCfg,
  Field,
  SettingRow,
  skipKeys,
  requiredKeys,
  fieldErrors,
}: {
  options: SectionOptions;
  cfg: SectionCfg;
  onCfg: (patch: Partial<SectionCfg>) => void;
  Field: React.ComponentType<FieldProps>;
  SettingRow: React.ComponentType<{
    label: string;
    options: string[];
    value: string;
    onChange: (v: string) => void;
    wrap?: boolean;
  }>;
  skipKeys?: Set<string>;
  /** Text fields that show a required asterisk (All Posts heading). */
  requiredKeys?: Set<string>;
  fieldErrors?: Record<string, string>;
}) {
  const CREATOR = useCreatorData();
  const entries = Object.entries(options)
    .filter(([key, o]) => {
      if (o.kind === "hidden" || skipKeys?.has(key)) return false;
      // Item / visible counts use feed defaults — hide from add & edit drawers.
      if (key === "count") return false;
      return true;
    })
    /* Drawer order: Layout → other fields → toggles → Heading → Background.
       Blog (JournalGrid) mockup: list|grid|magazine → toggles → Heading → bg. */
    .sort(([aKey, aOpt], [bKey, bOpt]) => {
      const rank = (key: string, o: SectionOption) => {
        if (key === "style" || key === "layout" || o.label === "Layout" || o.label === "Style")
          return 0;
        if (key === "bgTone" || o.label === "Background") return 90;
        if (key === "heading" || key === "lead" || key === "subtext" || key === "eyebrow")
          return 85;
        if (o.kind === "toggle") return 80;
        if (o.kind === "number") return 40;
        return 20;
      };
      return rank(aKey, aOpt) - rank(bKey, bOpt);
    });
  if (!entries.length) return null;

  return (
    <>
      {entries.map(([key, o]) => {
        const raw = cfg[key] != null ? cfg[key] : o.default;
        if (o.kind === "toggle") {
          return (
            <InspectorToggleRow
              key={key}
              label={o.label}
              on={Boolean(raw)}
              onChange={(v) => onCfg({ [key]: v })}
            />
          );
        }
        if (o.kind === "number") {
          return (
            <InspectorNumberRow
              key={key}
              label={o.label}
              value={Number(raw ?? o.default ?? 0)}
              min={o.min}
              max={o.max}
              onChange={(v) => onCfg({ [key]: v })}
            />
          );
        }
        if (o.kind === "text") {
          const required = requiredKeys?.has(key);
          const fieldError = String(fieldErrors?.[key] || "").trim();
          return (
            <React.Fragment key={key}>
              <Field
                label={required ? requiredFieldLabel(o.label) : o.label}
                reqField={required ? key : undefined}
                invalid={Boolean(fieldError)}
                immediate={required}
                value={String(raw ?? "")}
                placeholder={o.placeholder}
                onChange={(v) => onCfg({ [key]: v })}
              />
              {fieldError ? (
                <div
                  className="mte-we-field-hint mte-we-field-hint--error"
                  role="alert"
                  data-mte-publish-error="true"
                >
                  {fieldError}
                </div>
              ) : o.placeholder && !String(raw ?? "").trim() ? (
                <div className="mte-we-field-hint">{o.placeholder}</div>
              ) : null}
            </React.Fragment>
          );
        }
        if (o.kind === "textarea") {
          return (
            <Field
              key={key}
              label={o.label}
              multiline
              value={String(raw ?? "")}
              placeholder={o.placeholder}
              onChange={(v) => onCfg({ [key]: v })}
            />
          );
        }
        const opts = optionList(o, CREATOR.carousels);
        const value = String(raw ?? o.default ?? opts[0]?.value ?? "");
        if (o.kind === "select" || (o.kind === "radio" && opts.length > 5)) {
          return (
            <InspectorDropdown
              key={key}
              label={o.label}
              value={value}
              options={opts}
              onChange={(v) => onCfg({ [key]: coerceOptionValue(o, v) })}
            />
          );
        }
        if (o.kind === "radio") {
          const labels = opts.map((x) => x.label);
          const selected = opts.find((x) => x.value === value)?.label || labels[0];
          return (
            <SettingRow
              key={key}
              label={o.label}
              options={labels}
              value={selected}
              wrap={labels.join("").length > 16}
              onChange={(label) => {
                const match = opts.find((x) => x.label === label);
                onCfg({
                  [key]: coerceOptionValue(o, match ? match.value : label),
                });
              }}
            />
          );
        }
        return null;
      })}
    </>
  );
}

export function optionsForSectionType(sectionType: string): SectionOptions | null {
  const Comp = COMPONENTS[sectionType];
  return Comp?.options || null;
}

export function sectionCfgOf(
  tweaks: Tweaks | undefined,
  pageKey: string,
  sectionId: string,
): SectionCfg {
  return (tweaks?.pages?.[pageKey]?.cfg?.[sectionId] || {}) as SectionCfg;
}

/** Registry `props` that mirror editable cfg fields (PageHeader heading/lead, etc.). */
function cfgFieldsFromSectionProps(
  props?: Record<string, unknown>,
): Partial<SectionCfg> {
  if (!props) return {};
  const out: Partial<SectionCfg> = {};
  if (typeof props.heading === "string") out.heading = props.heading;
  if (typeof props.lead === "string") out.lead = props.lead;
  if (typeof props.eyebrow === "string") out.eyebrow = props.eyebrow;
  return out;
}

/** Inline-edit path slug for PageHeader (`pageHeader.{slug}.heading`). */
export function pageHeaderContentSlug(
  headingProp: string | undefined,
  sectionId?: string,
): string {
  // Built-in pages key off the registry heading. Added headers have no
  // heading prop — key off the section id so two headers don't share edits.
  const fromHeading = String(headingProp || "").trim();
  const source = fromHeading || String(sectionId || "page").trim() || "page";
  return source.replace(/\s+/g, "_").toLowerCase() || "page";
}

/**
 * Resolved cfg for Edit / Tweak UI — same layering the canvas uses:
 * component option defaults → page-section defaults → registry props →
 * saved cfg → contentEdits (section.* + pageHeader.* for headers).
 */
export function resolveSectionCfg(
  tweaks: Tweaks | undefined,
  pageKey: string,
  sectionId: string,
): SectionCfg {
  const spec = getPageDef(pageKey, tweaks)?.sections.find((s) => s.id === sectionId);
  const Comp = COMPONENTS[spec?.type || ""];
  const optionDefaults: SectionCfg = {};
  if (Comp?.options) {
    for (const [key, opt] of Object.entries(Comp.options)) {
      if (opt?.default !== undefined) optionDefaults[key] = opt.default as never;
    }
  }
  const userCfg = sectionCfgOf(tweaks, pageKey, sectionId);
  const propFields = cfgFieldsFromSectionProps(spec?.props);
  const extra = extraSectionsOf(tweaks?.pages?.[pageKey]).find(
    (s) => s.id === sectionId,
  );
  const catalogType = String(
    userCfg.blockType ||
      userCfg.catalogType ||
      spec?.catalogType ||
      extra?.catalogType ||
      "",
  ).trim();
  const schemaDefaults =
    catalogType &&
    (spec?.type === "SchemaBlock" || schemaBlockTypeForCatalog(catalogType))
      ? defaultSchemaCfg(catalogType)
      : null;
  const merged: SectionCfg = {
    ...optionDefaults,
    ...(spec?.defaults || {}),
    ...propFields,
    ...(schemaDefaults || {}),
    ...userCfg,
    ...(catalogType
      ? {
          blockType: String(userCfg.blockType || catalogType),
          catalogType: String(userCfg.catalogType || catalogType),
        }
      : {}),
  };
  let result = mergeSectionCfgFromContentEdits(
    pageKey,
    sectionId,
    merged,
    tweaks?.contentEdits,
  );

  // PageHeader canvas edits live under pageHeader.{slug}.* (not section.*).
  if (spec?.type === "PageHeader") {
    const slug = pageHeaderContentSlug(
      String(propFields.heading || ""),
      sectionId,
    );
    const edits = tweaks?.contentEdits;
    if (edits) {
      const hKey = `pageHeader.${slug}.heading`;
      const lKey = `pageHeader.${slug}.lead`;
      if (Object.prototype.hasOwnProperty.call(edits, hKey)) {
        result = { ...result, heading: edits[hKey] };
      }
      if (Object.prototype.hasOwnProperty.call(edits, lKey)) {
        result = { ...result, lead: edits[lKey] };
      }
    }
  }

  // Blog "All posts" heading — never-touched sites have empty cfg; fill the
  // default so Publish / leave-guard don't trip "Blog header is empty".
  if (spec?.type === "JournalGrid") {
    const headingPath = `section.${pageKey}/${sectionId}.heading`;
    const explicitlyCleared =
      tweaks?.contentEdits != null &&
      Object.prototype.hasOwnProperty.call(tweaks.contentEdits, headingPath) &&
      !String(tweaks.contentEdits[headingPath] || "").trim();
    if (!explicitlyCleared && !String(result.heading || "").trim()) {
      result = { ...result, heading: DEFAULT_JOURNAL_GRID_HEADING };
    }
  }

  return result;
}

/** Coerce a radio/select string back to the original option type (keeps number columns). */
export function coerceOptionValue(
  option: SectionOption,
  next: string,
): string | number | boolean {
  const raw = (option.options || []).find((opt) => {
    if (typeof opt === "object" && opt != null && "value" in opt) {
      return String(opt.value) === next;
    }
    return String(opt) === next;
  });
  if (typeof raw === "number") return raw;
  if (typeof raw === "boolean") return raw;
  if (typeof raw === "object" && raw != null && "value" in raw) {
    const v = (raw as { value: string | number }).value;
    return typeof v === "number" ? v : String(v);
  }
  if (option.kind === "number") {
    const n = Number(next);
    return Number.isFinite(n) ? n : next;
  }
  return next;
}

export function isPageSectionVisible(
  tweaks: Tweaks | undefined,
  pageKey: string,
  sectionId: string,
): boolean {
  const def = getPageDef(pageKey, tweaks)?.sections.find((s) => s.id === sectionId);
  const hidden = tweaks?.pages?.[pageKey]?.hidden || {};
  if (def?.defaultHidden) return hidden[sectionId] === false;
  return hidden[sectionId] !== true;
}
